'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { createPath } from 'DesignData/helpers/routingHelper';
import { isEmpty } from 'underscore';
import { tryParseNumber, formatPathAsId } from 'DesignData/lib/text';
import { LAYOUT, TEMPLATE, CUSTOM_WIDGET, GLOBAL_WIDGET, NONE as DESIGN_ASSET_TYPE_NONE, RAW_ASSET, FOLDER, SETTINGS, WELCOME, LAYOUT_SOURCE, GETTING_STARTED } from 'DesignData/enums/DesignAssetTypes';
import Tab from 'DesignData/models/Tab';
import { getTab, getLastOpenTab } from 'DesignData/selectors/tabs';
import { getById } from 'DesignData/selectors/layouts';
import { QUERY_STRING_SHOW_COMPILED_HUBL } from 'DesignData/constants/Routing';
import { showGlobalContentMessage, setActiveEditor } from 'DesignData/actions/Application';
import { fetch as fetchUnifiedLayouts } from 'DesignData/actions/UnifiedLayouts';
import { openTab, updateTab } from 'DesignData/actions/Tabs';
import { openByPath } from 'DesignData/actions/TemplateBuffers';
import Application from 'DesignManagerIde/containers/Application';

const getAssetParams = (assetType, byPath, params) => {
  let id;
  const rId = tryParseNumber(params.revisionId);

  switch (assetType) {
    case LAYOUT_SOURCE:
    case LAYOUT:
      {
        id = tryParseNumber(params.assetId);
        break;
      }

    case TEMPLATE:
    case CUSTOM_WIDGET:
      {
        id = byPath ? formatPathAsId(params[0]) : tryParseNumber(params.assetId);
        break;
      }

    case GLOBAL_WIDGET:
      {
        id = tryParseNumber(params.assetId);
        break;
      }

    case RAW_ASSET:
      {
        assetType = RAW_ASSET;
        id = byPath ? formatPathAsId(params[0]) : tryParseNumber(params.assetId);
        break;
      }

    case FOLDER:
      {
        id = tryParseNumber(params.assetId);
        break;
      }

    case SETTINGS:
      {
        id = 'settings';
        break;
      }

    case GETTING_STARTED:
      {
        id = 'gettingStarted';
        break;
      }

    case WELCOME:
      {
        id = 'welcome';
        break;
      }

    default:
      break;
  }

  return {
    id,
    rId
  };
};

const RoutingWrapper = (_ref) => {
  let {
    assetSource,
    assetType,
    byPath,
    attemptToShowCompiledOutput,
    handleOnLeave,
    handleOnChange,
    isEmptyState,
    isLayoutSource,
    MainComponent,
    FinderComponent,
    SettingsEditor,
    settingsCategory
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["assetSource", "assetType", "byPath", "attemptToShowCompiledOutput", "handleOnLeave", "handleOnChange", "isEmptyState", "isLayoutSource", "MainComponent", "FinderComponent", "SettingsEditor", "settingsCategory"]);

  const dispatch = useDispatch();
  const history = useHistory();
  const queryParams = useParams();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const tab = useSelector(state => getTab(state, {
    tabId: tryParseNumber(queryParams.assetId)
  }));
  const lastOpenTab = useSelector(getLastOpenTab);
  const layoutsById = useSelector(getById);
  const {
    id: assetId,
    rId: revisionId
  } = getAssetParams(assetType, byPath, queryParams);
  const prevTabRef = useRef();

  const onEnter = nextState => {
    if (query.has('templatePath')) {
      dispatch(openByPath(query.get('templatePath')));
    }

    if (query.get('referrer') === 'global-content') {
      dispatch(showGlobalContentMessage());
    }

    const editorOptions = {
      revisionId,
      assetSource: assetSource || null,
      settingsCategory
    }; // Set showCompiledOutput in tab based on editor options

    if (attemptToShowCompiledOutput) {
      const showCompiledOutputFromQuery = false;
      editorOptions.showCompiledOutput = showCompiledOutputFromQuery;
    }

    if (isEmptyState) {
      if (assetType === DESIGN_ASSET_TYPE_NONE) return;

      if (lastOpenTab) {
        const path = createPath(lastOpenTab.assetType, lastOpenTab.id, lastOpenTab.editorOptions);

        if (path !== '/') {
          history.replace({
            pathname: path,
            state: {
              nextPathname: nextState.location.pathname
            }
          });
        }
      }

      dispatch(setActiveEditor(null, null, {}));
      return;
    }

    if (assetType === DESIGN_ASSET_TYPE_NONE) return;
    dispatch(setActiveEditor(assetType, assetId, editorOptions));
    dispatch(openTab(new Tab({
      assetType,
      id: assetId,
      editorOptions
    }), false));
  };

  const onChange = () => {
    const editorOptions = {
      revisionId
    };

    if (attemptToShowCompiledOutput) {
      const showCompiledOutputFromQuery = !!query.has(QUERY_STRING_SHOW_COMPILED_HUBL);
      editorOptions.showCompiledOutput = showCompiledOutputFromQuery;
      const tabToUpdate = {
        id: assetId,
        assetType
      };
      dispatch(updateTab(tabToUpdate, {
        editorOptions
      }));
    }
  };

  const onLeave = () => {
    if (prevTabRef.current) {
      dispatch(updateTab(prevTabRef.current, {
        lastActiveAt: Date.now()
      }));
    }
  }; // Handle Routing updates on mount (Replaces onEnter behavior)


  useEffect(() => {
    if (handleOnLeave) {
      onLeave();
    }

    onEnter({
      location: {
        pathname: '/'
      }
    });
    prevTabRef.current = tab; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleOnLeave, isLayoutSource, rest.match.url]); // Handle Routing changes (Replaces onChange behavior)

  useEffect(() => {
    if (handleOnChange) {
      onChange();
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [handleOnChange, rest.match.url]); // Ensures that the source template for layouts displays properly with a page refresh

  useEffect(() => {
    if (isEmpty(layoutsById) && isLayoutSource) {
      dispatch(fetchUnifiedLayouts(assetId));
    }
  }, [layoutsById, isLayoutSource, dispatch, assetId]);
  return /*#__PURE__*/_jsx(Application, Object.assign({}, rest, {
    mainComponent: MainComponent ? /*#__PURE__*/_jsx(MainComponent, {}) : null,
    finderComponent: FinderComponent ? /*#__PURE__*/_jsx(FinderComponent, {}) : null,
    settingsEditor: SettingsEditor ? /*#__PURE__*/_jsx(SettingsEditor, {}) : null
  }));
};

RoutingWrapper.propTypes = {
  byPath: PropTypes.bool,
  assetSource: PropTypes.string,
  assetType: PropTypes.number,
  attemptToShowCompiledOutput: PropTypes.bool,
  settingsCategory: PropTypes.string,
  handleOnLeave: PropTypes.bool,
  isEmptyState: PropTypes.bool,
  handleOnChange: PropTypes.bool,
  isLayoutSource: PropTypes.bool,
  MainComponent: PropTypes.elementType.isRequired,
  SettingsEditor: PropTypes.elementType,
  FinderComponent: PropTypes.elementType
};
RoutingWrapper.defaultProps = {
  assetType: DESIGN_ASSET_TYPE_NONE
};
export default RoutingWrapper;